<template>
    <div
        v-bind:class="responsiveClasses.outerRow"
        class="row g-0"
        data-test-role="table-pagination">
        <div
            v-bind:class="responsiveClasses.pageButtonRow"
            class="d-flex col-12">
            <div
                v-show="currentPage > 1"
                class="p-1 cursor-pointer align-self-center"
                data-test-role="page-prev"
                data-gtmid="pagination-link-prev"
                v-on:click="prevPage">
                <pc-bs-icon name="chevron-left" />
            </div>
            <div
                v-for="(link, index) in pageLinks"
                v-show="link.show"
                v-bind:key="index"
                v-bind:data-gtmid="link.gtmid"
                v-bind:class="link.css"
                v-bind:data-test-role="link.testRole"
                class="p-2 mx-1"
                v-on:click="link.click">
                {{ link.text }}
            </div>
            <div
                v-show="currentPage < numberOfPages"
                class="p-1 cursor-pointer align-self-center"
                data-test-role="page-next"
                data-gtmid="pagination-link-next"
                data-bind="click:nextPage"
                v-on:click="nextPage">
                <pc-bs-icon name="chevron-right" />
            </div>
        </div>
        <div
            v-show="showAllPages"
            class="mb-2"
            v-bind:class="responsiveClasses.selectWrapper">
            <pc-select
                v-bind:options="selectOptions"
                v-bind:model-value="currentPage"
                alt-label="Page"
                v-on:update:model-value="setCurrentPageNumber" />
        </div>
    </div>
</template>

<script>
    import { getResponsiveClassMap } from "Responsive/manager";
    const responsiveConfig = {
        outerRow: {
            "justify-content": { xs: "center", md: "end" }
        },
        pageButtonRow: {
            "justify-content": { xs: "center", md: "end" }
        },
        selectWrapper: {
            col: { xs: 4, md: 2, lg: 1 }
        }
    };

    export default {
        inject: {
            viewport: {
                from: "viewport",
                default: null
            }
        },
        props: {
            currentPage: { type: Number, required: true },
            numberOfPages: { type: Number, required: true }
        },
        emits: ["page-changed"],
        data: function () {
            return {
                showAllPages: false
            };
        },
        computed: {
            selectOptions() {
                const res = [];
                for (let i = 1; i <= this.numberOfPages; i++) {
                    res.push({
                        value: i,
                        text: i
                    });
                }
                return res;
            },
            pageLinks() {
                //set up page link structures: 1st, ..., n-2, n-1, n, n+1, n+2, ..., last
                const links = [];
                links.push({
                    text: 1,
                    show: this.currentPage > 3,
                    click: () => this.setCurrentPageNumber(1),
                    css: "pagination-link",
                    gtmid: "pagination-link-1",
                    testRole: "page-link-first"
                });
                links.push({
                    text: "...",
                    show: this.currentPage - 3 > 1,
                    click: () => this.setShowAllPagesTrue(),
                    css: "pagination-link",
                    gtmid: "pagination-link-ellipsis-prev"
                });
                for (let offset = -2; offset <= 2; offset++) {
                    const pageNum = this.currentPage + offset;
                    links.push({
                        text: pageNum,
                        show: pageNum >= 1 && pageNum <= this.numberOfPages,
                        click: () => this.setCurrentPageNumber(pageNum),
                        css: offset === 0 ? "pagination-current" : "pagination-link",
                        gtmid: `pagination-link-${pageNum}`,
                        testRole: `page-link-${pageNum}`
                    });
                }
                links.push({
                    text: "...",
                    show: this.currentPage + 3 < this.numberOfPages,
                    click: () => this.setShowAllPagesTrue(),
                    css: "pagination-link",
                    gtmid: "pagination-link-ellipsis-next"
                });
                links.push({
                    text: this.numberOfPages,
                    show: this.currentPage <= this.numberOfPages - 3,
                    click: () => this.setCurrentPageNumber(this.numberOfPages),
                    css: "pagination-link",
                    gtmid: `pagination-link-${this.numberOfPages}`,
                    testRole: "page-link-last"
                });
                return links;
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            }
        },
        methods: {
            setShowAllPagesTrue() {
                this.showAllPages = true;
            },
            setCurrentPageNumber(number) {
                if (number > 0 && number <= this.numberOfPages && number !== this.currentPage) {
                    this.$emit("page-changed", number);
                }
            },
            prevPage() {
                this.setCurrentPageNumber(this.currentPage - 1);
            },
            nextPage() {
                this.setCurrentPageNumber(this.currentPage + 1);
            }
        }
    };
</script>

<style lang="scss" scoped>
    .page-next-icon,
    .page-prev-icon {
        background-image: url("Images/icons/sideArrow.svg");
        width: 15px;
        height: 15px;
        background-size: 15px 15px;
        filter: grayscale(100%) brightness(5) brightness(0.7);
    }
    .page-prev-icon {
        transform: rotate(180deg);
    }
    .pagination-link {
        color: var(--bs-primary);
        cursor: pointer;
    }
    .pagination-current {
        color: var(--bs-black);
        font-weight: bold;
        background: var(--bs-gray-400);
        cursor: default;
    }
</style>
